import { createRng } from '@battle/sim';
import {
  color,
  float,
  fract,
  hash,
  instanceIndex,
  mix,
  positionLocal,
  sin,
  time,
  vec3,
} from 'three/tsl';
import * as THREE from 'three/webgpu';

/**
 * Procedural stand-in for Kilnback (docs/03, concept K-A): a pangolin with ceramic kiln-tile
 * scales over a glowing magma core, a blunt snout, stubby legs and an ember-club tail.
 * Replaced by the real modeled and rigged creature in M4.
 */
export interface Creature {
  root: THREE.Group;
  update(dt: number, elapsed: number): void;
}

// Body ellipsoid (meters), centered above the ground.
const BODY = { rx: 0.78, ry: 0.62, rz: 1.15, y: 0.92 };

export function createKilnbackPlaceholder(): Creature {
  const rng = createRng(4242);
  const root = new THREE.Group();
  root.name = 'kilnback';
  const body = new THREE.Group();
  root.add(body);

  // Magma: HDR emissive that breathes, so bloom picks it up.
  const heat = sin(time.mul(1.7)).mul(0.25).add(0.75);
  const magmaMat = new THREE.MeshStandardNodeMaterial({ roughness: 0.6 });
  magmaMat.colorNode = color('#2a0d04');
  magmaMat.emissiveNode = mix(color('#ff4a0a'), color('#ffc35a'), heat).mul(heat.mul(4).add(2));

  const scaleMat = new THREE.MeshStandardNodeMaterial({
    roughness: 0.42,
    metalness: 0.05,
    flatShading: true,
  });
  scaleMat.colorNode = color('#ffffff'); // per-plate instance colors carry the palette
  const tailMat = new THREE.MeshStandardNodeMaterial({ color: '#6a3520', roughness: 0.5 });
  const hideMat = new THREE.MeshStandardNodeMaterial({ color: '#3a2a20', roughness: 0.85 });
  const clawMat = new THREE.MeshStandardNodeMaterial({ color: '#d9c7a4', roughness: 0.4 });
  const eyeMat = new THREE.MeshStandardNodeMaterial({ color: '#000000' });
  eyeMat.emissiveNode = color('#ffb020').mul(4);

  const shadow = (m: THREE.Mesh) => {
    m.castShadow = true;
    m.receiveShadow = true;
    return m;
  };

  // Glowing core, slightly smaller than the plate shell: visible through the seams.
  const core = shadow(new THREE.Mesh(new THREE.SphereGeometry(1, 40, 28), magmaMat));
  core.scale.set(BODY.rx * 0.93, BODY.ry * 0.93, BODY.rz * 0.93);
  core.position.y = BODY.y;
  body.add(core);

  // Belly (unarmored hide).
  const belly = shadow(new THREE.Mesh(new THREE.SphereGeometry(1, 32, 20), hideMat));
  belly.scale.set(BODY.rx * 0.9, BODY.ry * 0.7, BODY.rz * 0.92);
  belly.position.y = BODY.y - 0.18;
  body.add(belly);

  // Shingled plates: flat hexagonal "kiln tiles" with a bevel, oriented to the ellipsoid surface
  // with the trailing edge lifted. The gaps between tiles show the glowing magma core.
  const plateGeo = new THREE.CylinderGeometry(1, 0.82, 0.16, 6, 1);
  plateGeo.rotateY(Math.PI / 6); // a point faces the tail
  plateGeo.scale(0.95, 1, 1.15);
  const rows = 10;
  const perRow = 8;
  // Cylinder groups: 0 = rim, 1 = top, 2 = bottom. Glowing rims read as magma seams.
  const plates = new THREE.InstancedMesh(
    plateGeo,
    [magmaMat, scaleMat, scaleMat],
    rows * perRow + 12,
  );
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3();
  const back = new THREE.Vector3(0, 0, -1);
  const pos = new THREE.Vector3();
  const scl = new THREE.Vector3();
  const basis = new THREE.Matrix4();
  const tangent = new THREE.Vector3();
  const bitangent = new THREE.Vector3();
  const tint = new THREE.Color();
  const palette = [
    new THREE.Color('#8a4a2a'),
    new THREE.Color('#a45a32'),
    new THREE.Color('#5e3322'),
  ];
  let n = 0;
  const placePlate = (s: number, phi: number, size: number) => {
    const k = Math.sqrt(Math.max(1 - s * s, 0));
    pos.set(BODY.rx * k * Math.sin(phi), BODY.y + BODY.ry * k * Math.cos(phi), BODY.rz * s);
    up.set(pos.x / BODY.rx ** 2, (pos.y - BODY.y) / BODY.ry ** 2, pos.z / BODY.rz ** 2).normalize();
    // Tangent pointing toward the tail, then tilt so the trailing edge lifts (shingle overlap).
    tangent.copy(back).addScaledVector(up, -back.dot(up)).normalize();
    up.applyAxisAngle(bitangent.crossVectors(up, tangent).normalize(), -0.32);
    // Right-handed basis: x = bitangent, y = tilted normal, z = bitangent x normal (toward tail).
    tangent.crossVectors(bitangent, up).normalize();
    basis.makeBasis(bitangent, up, tangent);
    q.setFromRotationMatrix(basis);
    scl.setScalar(size);
    m.compose(pos.addScaledVector(up, 0.02), q, scl);
    plates.setMatrixAt(n, m);
    tint
      .copy(palette[rng.int(0, palette.length - 1)] as THREE.Color)
      .multiplyScalar(0.85 + rng.next() * 0.3);
    plates.setColorAt(n, tint);
    n++;
  };
  for (let r = 0; r < rows; r++) {
    const s = 0.88 - (r / (rows - 1)) * 1.7; // head (+z) to tail (-z)
    const k = Math.sqrt(1 - s * s);
    const offset = r % 2 ? 0.5 : 0;
    for (let i = 0; i < perRow; i++) {
      const phi = ((i + offset) / (perRow - 1) - 0.5) * Math.PI * 1.25;
      placePlate(s, phi, 0.15 + 0.15 * k);
    }
  }
  // Head plates.
  for (let i = 0; i < 12; i++) placePlate(0.9 + (i % 3) * 0.03, ((i % 4) / 3 - 0.5) * 1.4, 0.13);
  plates.count = n;
  plates.castShadow = true;
  plates.receiveShadow = true;
  body.add(plates);

  // Head: skull, snout, eyes.
  const head = new THREE.Group();
  head.position.set(0, BODY.y - 0.05, BODY.rz * 0.92);
  body.add(head);
  const skull = shadow(new THREE.Mesh(new THREE.SphereGeometry(0.36, 28, 20), hideMat));
  skull.scale.set(1, 0.85, 1.15);
  head.add(skull);
  const snout = shadow(new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.62, 20), hideMat));
  snout.rotation.x = Math.PI / 2;
  snout.position.set(0, -0.08, 0.5);
  head.add(snout);
  const nose = shadow(new THREE.Mesh(new THREE.SphereGeometry(0.045, 12, 8), hideMat));
  nose.position.set(0, -0.08, 0.8);
  head.add(nose);
  for (const side of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.055, 16, 12), eyeMat);
    eye.position.set(side * 0.2, 0.08, 0.26);
    head.add(eye);
  }

  // Legs with claws.
  const legGeo = new THREE.CylinderGeometry(0.15, 0.2, 0.62, 12);
  const clawGeo = new THREE.ConeGeometry(0.035, 0.16, 8);
  const legs: THREE.Group[] = [];
  for (const [x, z] of [
    [-0.55, 0.62],
    [0.55, 0.62],
    [-0.55, -0.62],
    [0.55, -0.62],
  ] as const) {
    const leg = new THREE.Group();
    leg.position.set(x, 0.62, z);
    const shin = shadow(new THREE.Mesh(legGeo, hideMat));
    shin.position.y = -0.31;
    leg.add(shin);
    for (let k = -1; k <= 1; k++) {
      const claw = shadow(new THREE.Mesh(clawGeo, clawMat));
      claw.rotation.x = Math.PI / 2.4;
      claw.position.set(k * 0.07, -0.6, 0.16);
      leg.add(claw);
    }
    legs.push(leg);
    body.add(leg);
  }

  // Tail: a chain of armored segments ending in the ember club.
  const tailRoot = new THREE.Group();
  tailRoot.position.set(0, BODY.y - 0.05, -BODY.rz * 0.95);
  body.add(tailRoot);
  const tailSegments: THREE.Group[] = [];
  let parent: THREE.Object3D = tailRoot;
  const segGeo = new THREE.SphereGeometry(1, 20, 14);
  for (let i = 0; i < 7; i++) {
    const seg = new THREE.Group();
    seg.position.set(0, i === 0 ? 0 : -0.02, i === 0 ? 0 : -0.3);
    seg.rotation.x = i === 0 ? 0.35 : -0.08;
    const radius = 0.3 - i * 0.03;
    const armor = shadow(new THREE.Mesh(segGeo, tailMat));
    armor.scale.set(radius, radius * 0.8, radius * 1.3);
    seg.add(armor);
    parent.add(seg);
    tailSegments.push(seg);
    parent = seg;
  }
  const club = new THREE.Group();
  club.position.set(0, 0.02, -0.34);
  parent.add(club);
  const clubShell = shadow(new THREE.Mesh(new THREE.DodecahedronGeometry(0.24, 0), tailMat));
  club.add(clubShell);
  const clubEmber = new THREE.Mesh(new THREE.OctahedronGeometry(0.2, 0), magmaMat);
  clubEmber.rotation.set(0.4, 0.3, 0);
  clubEmber.scale.setScalar(1.12);
  club.add(clubEmber);

  // Embers rising off the back.
  const emberCount = 60;
  const emberMat = new THREE.MeshBasicNodeMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const h = hash(instanceIndex);
  const life = fract(time.mul(float(0.22).add(h.mul(0.2))).add(h.mul(7.3)));
  emberMat.positionNode = positionLocal.add(
    vec3(
      sin(time.mul(2).add(h.mul(40)))
        .mul(0.12)
        .mul(life),
      life.mul(1.8),
      life.mul(-0.4),
    ),
  );
  emberMat.colorNode = mix(color('#ffd27a'), color('#ff3d00'), life).mul(float(1).sub(life).mul(6));
  const embers = new THREE.InstancedMesh(
    new THREE.OctahedronGeometry(0.018, 0),
    emberMat,
    emberCount,
  );
  for (let i = 0; i < emberCount; i++) {
    const s = (rng.next() - 0.5) * 1.4;
    const phi = (rng.next() - 0.5) * 1.6;
    const k = Math.sqrt(Math.max(1 - s * s, 0));
    m.makeTranslation(
      BODY.rx * k * Math.sin(phi),
      BODY.y + BODY.ry * k * Math.cos(phi),
      BODY.rz * s,
    );
    embers.setMatrixAt(i, m);
  }
  embers.frustumCulled = false;
  body.add(embers);

  // A warm light from the magma so the creature lights its surroundings.
  const glow = new THREE.PointLight(0xff7a2f, 3, 6, 2);
  glow.position.set(0, BODY.y + 0.2, 0);
  body.add(glow);

  return {
    root,
    update(_dt, t) {
      // Idle: breathing, head bob and look-around, tail sway, weight shift.
      const breath = Math.sin(t * 1.6);
      body.scale.set(1 + breath * 0.012, 1 + breath * 0.02, 1);
      body.position.y = Math.sin(t * 0.8) * 0.015;
      head.rotation.set(
        Math.sin(t * 1.3) * 0.05 - 0.05,
        Math.sin(t * 0.45) * 0.35,
        Math.sin(t * 0.7) * 0.04,
      );
      tailSegments.forEach((seg, i) => {
        seg.rotation.y = Math.sin(t * 1.4 - i * 0.55) * 0.12;
      });
      club.rotation.z = Math.sin(t * 1.4 - 4) * 0.2;
      legs.forEach((leg, i) => {
        leg.rotation.x = Math.sin(t * 0.8 + i) * 0.03;
      });
      glow.intensity = 2.5 + Math.sin(t * 1.7) * 0.8;
    },
  };
}
